import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Button as MuiButton,
} from '@mui/material';
import axiosInstant from '../../../api';
import config from '../../../config';
import { adminScoresSettings, adminAttributes } from '../../../api/endpoints';
import { Button } from '../../../components/Button/Button';
import { DotLottleLoader } from '../../../components/Loader';
import { IAttribute } from '../attributes/constants';

// A universal (catch-all) value is stored as an { attribute, value } name pair.
export interface UniversalValue {
  attribute: string;
  value: string;
}

interface Props {
  // The currently-saved universal values, loaded from GET /admin/scores/settings.
  universalValues: UniversalValue[];
  onAfterSave: () => void;
}

const settingsUrl = `${config.API_URL}${adminScoresSettings}`;
const attrUrl = `${config.API_URL}${adminAttributes}`;

const pairKey = (attribute: string, value: string) =>
  `${attribute.toLowerCase()}|${value.toLowerCase()}`;

// Equal-membership check over two id sets (selection vs the loaded baseline).
const setsEqual = (a: Set<number>, b: Set<number>) =>
  a.size === b.size && [...a].every((x) => b.has(x));

export const UniversalValuesCard: React.FC<Props> = ({ universalValues, onAfterSave }) => {
  const [attributes, setAttributes] = useState<IAttribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Selection + loaded baseline, keyed by attribute_value id (ids are unique).
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [original, setOriginal] = useState<Set<number>>(new Set());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstant.get(attrUrl);
        if (active) setAttributes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (active) toast.error('Failed to load attributes');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Resolve the saved { attribute, value } name pairs to value ids once both the
  // attribute catalog and the saved list are available. Re-syncs the baseline
  // whenever either changes (e.g. after a save → parent refetch).
  useEffect(() => {
    if (attributes.length === 0) return;
    const idByPair = new Map<string, number>();
    for (const attr of attributes) {
      for (const v of attr.values ?? []) {
        idByPair.set(pairKey(attr.name, v.name), v.id);
      }
    }
    const resolved = new Set<number>();
    for (const uv of universalValues ?? []) {
      const id = idByPair.get(pairKey(uv.attribute, uv.value));
      if (id != null) resolved.add(id);
    }
    setSelected(resolved);
    setOriginal(resolved);
  }, [attributes, universalValues]);

  // Only attributes that have at least one value are worth showing.
  const sortedAttributes = useMemo(
    () =>
      [...attributes]
        .filter((a) => (a.values ?? []).length > 0)
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    [attributes],
  );

  const dirty = !setsEqual(selected, original);

  const toggle = (valueId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(valueId)) next.delete(valueId);
      else next.add(valueId);
      return next;
    });
  };

  const reset = () => setSelected(original);

  const save = async () => {
    setSaving(true);
    try {
      const payload: UniversalValue[] = [];
      for (const attr of attributes) {
        for (const v of attr.values ?? []) {
          if (selected.has(v.id)) payload.push({ attribute: attr.name, value: v.name });
        }
      }
      await axiosInstant.patch(settingsUrl, { universalValues: payload });
      toast.success('Universal values saved — run a recompute to apply them to scores');
      onAfterSave();
    } catch (err) {
      toast.error('Failed to save universal values');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box className="flex items-center justify-between pb-1">
        <Typography fontWeight={600}>Universal (catch-all) values</Typography>
        <MuiButton size="small" variant="outlined" onClick={reset} disabled={!dirty}>
          Reset
        </MuiButton>
      </Box>
      <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
        Mark attribute values that mean &ldquo;applies to every value of this
        attribute&rdquo; (e.g. Age Group → Universal, Gender Focus → Unisex). When
        either an expert or a product carries a selected value, that whole
        attribute scores a full match instead of needing the exact same values.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {loading && (
        <Box className="flex justify-center py-6">
          <DotLottleLoader />
        </Box>
      )}

      {!loading && (
        <Box className="flex flex-col gap-4">
          {sortedAttributes.map((attr) => (
            <Box key={attr.id} className="flex flex-col gap-2">
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{attr.name}</Typography>
              <Box className="flex flex-wrap gap-2">
                {[...(attr.values ?? [])]
                  .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
                  .map((v) => {
                    const isOn = selected.has(v.id);
                    return (
                      <Chip
                        key={v.id}
                        label={v.name}
                        onClick={() => toggle(v.id)}
                        variant={isOn ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isOn ? 'var(--accent)' : 'transparent',
                          color: isOn ? '#fff' : 'var(--text)',
                          borderColor: isOn ? 'var(--accent)' : 'var(--border)',
                          '&:hover': { bgcolor: isOn ? '#977343' : 'rgba(171,133,74,0.08)' },
                        }}
                      />
                    );
                  })}
              </Box>
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
          {sortedAttributes.length === 0 && (
            <Typography className="py-4" color="var(--text-2)" textAlign="center">
              No attributes with values found.
            </Typography>
          )}
        </Box>
      )}

      <Box className="flex justify-end pt-4">
        <Button onClick={save} disabled={!dirty || saving} className="!px-6">
          <Typography>{saving ? 'Saving…' : 'Save universal values'}</Typography>
        </Button>
      </Box>
    </Paper>
  );
};
